import CustomButton from 'components/Buttons/CustomButton'
import { useState, useEffect, useMemo, useRef } from 'react'
import QRCode from 'react-qr-code'
import { 
  Printer, 
  Smartphone, 
  ChevronLeft, 
  ChevronRight, 
  X, 
  List, 
  CheckSquare, 
  Square,
  Search,
  Check,
  RefreshCw,
  Box
} from 'react-feather'
import { 
  Card, 
  CardBody, 
  Alert, 
  Spinner, 
  Progress, 
  Input, 
  Label, 
  FormGroup,
  InputGroup,
  InputGroupText,
  Badge
} from 'reactstrap'
import FormatProcessor from 'utils/FormatProcessor'
import { thermalPrinter } from 'utils/thermalPrinter'
import { renderQrWithLogoToCanvas } from 'utils/qrLogoHelper'
import pmlogo from 'assets/images/pmlogo.png'

export default function BulkIDStockModal({ 
  isOpen, 
  onClose, 
  selectedStocks = [], 
  initialSelectedIds = [],
  totalItems = 0,
  onLoadMore = () => {},
  onSelectAll = () => {},
  isLoading = false
}) {
  const qrRefs = useRef({})
  const logoRef = useRef(null)
  const [viewMode, setViewMode] = useState('selection') // 'selection' or 'preview'
  const [selectedIds, setSelectedIds] = useState([])
  const [currentIndex, setCurrentIndex] = useState(0)
  const [isConnected, setIsConnected] = useState(thermalPrinter.isConnected)
  const [isConnecting, setIsConnecting] = useState(false)
  const [isPrinting, setIsPrinting] = useState(false)
  const [printProgress, setPrintProgress] = useState(0)
  const [searchTerm, setSearchTerm] = useState('')
  const [localSearchTerm, setLocalSearchTerm] = useState('')
  const [shouldSelectAll, setShouldSelectAll] = useState(false)

  useEffect(() => {
    const handler = setTimeout(() => {
      setSearchTerm(localSearchTerm)
    }, 200)

    return () => {
      clearTimeout(handler)
    }
  }, [localSearchTerm])
  
  const listRef = useRef(null)
  const inventoryUrl = process.env.REACT_APP_INVENTORY_URL
  
  const getItemName = (stock) => {
    return stock?.item?.nameItem || stock?.nameItem || stock?.name || stock?.itemName || stock?.label || 'Tanpa Nama'
  }

  useEffect(() => {
    if (isOpen) {
      // Use initialSelectedIds from the main table
      setSelectedIds(initialSelectedIds)
      setViewMode('selection')
      setCurrentIndex(0)
      setSearchTerm('')
      setLocalSearchTerm('')
    }
  }, [isOpen])

  const displayStocks = useMemo(() => {
    if (!searchTerm) return selectedStocks
    return selectedStocks.filter(s => 
      getItemName(s).toLowerCase().includes(searchTerm.toLowerCase()) ||
      String(s.id).includes(searchTerm)
    )
  }, [selectedStocks, searchTerm])

  const filteredStocks = useMemo(() => {
    return selectedStocks.filter(s => selectedIds.includes(s.id))
  }, [selectedStocks, selectedIds])

  const currentStock = filteredStocks[currentIndex]
  
  const getUrl = (stock) => `${inventoryUrl}?productId=${stock?.traceCode}`

  const handleToggleConnect = async () => {
    if (isConnected) {
      await thermalPrinter.disconnect()
      setIsConnected(false)
    } else {
      setIsConnecting(true)
      try {
        const success = await thermalPrinter.connect()
        if (success) setIsConnected(true)
      } catch (err) {
        console.error('Connection failed:', err)
      } finally {
        setIsConnecting(false)
      }
    }
  }



  const handlePrintOne = async () => {
    if (!isConnected || !currentStock) return

    setIsPrinting(true)
    try {
      await printItem(currentStock)
    } catch (err) {
      console.error('Print failed:', err)
    } finally {
      setIsPrinting(false)
    }
  }

  const handlePrintAll = async () => {
    if (!isConnected || filteredStocks.length === 0) return

    setIsPrinting(true)
    setPrintProgress(0)
    try {
      for (let i = 0; i < filteredStocks.length; i++) {
        setPrintProgress(Math.round(((i + 1) / filteredStocks.length) * 100))
        await printItem(filteredStocks[i])
        // Small delay between items to prevent buffer overflow or overheating
        await new Promise(resolve => setTimeout(resolve, 800))
      }
    } catch (err) {
      console.error('Bulk print failed:', err)
    } finally {
      setIsPrinting(false)
      setPrintProgress(0)
    }
  }

  const getQrCanvas = async (stock) => {
    const svgContainer = qrRefs.current[stock.id]
    if (!svgContainer) return null
    
    const svgElement = svgContainer.querySelector('svg')
    const logoElement = logoRef.current
    
    return await renderQrWithLogoToCanvas(svgElement, logoElement, 384)
  }

  const printItem = async (stock) => {
    const title = getItemName(stock)
    const subtitle = `ID: ${stock?.id || '-'}`
    
    // Use the same canvas logic as the successful TransactionQRPrint page
    const canvas = await getQrCanvas(stock)

    if (canvas) {
      await thermalPrinter.printQrAsImage(title, subtitle, canvas)
    } else {
      // Fallback
      const url = getUrl(stock)
      const qrValue = FormatProcessor.generate('searchInventory', {
        id: stock?.id,
        name: title,
        gtin: stock?.gtin || '',
        expiry: stock?.expiry || '',
        traceCode: stock?.traceCode || '',
        url,
        productDescription: title,
      })
      await thermalPrinter.printQrItem(title, subtitle, qrValue)
    }
  }

  const toggleSelectAll = async () => {
    if (searchTerm) {
      const displayIds = displayStocks.map(s => s.id)
      const allSelected = displayStocks.length > 0 && displayStocks.every(s => selectedIds.includes(s.id))
      
      if (allSelected) {
        // Deselect only the items matching the search query
        setSelectedIds(prev => prev.filter(id => !displayIds.includes(id)))
      } else {
        // Select only the items matching the search query
        setSelectedIds(prev => {
          const next = [...prev]
          displayIds.forEach(id => {
            if (!next.includes(id)) {
              next.push(id)
            }
          })
          return next
        })
      }
      return
    }

    if (selectedIds.length === totalItems && totalItems > 0) {
      setSelectedIds([])
      setShouldSelectAll(false)
    } else {
      // If we haven't loaded everything, fetch all first
      if (selectedStocks.length < totalItems) {
        setShouldSelectAll(true)
        await onSelectAll()
      } else {
        setSelectedIds(selectedStocks.map(s => s.id))
      }
    }
  }

  // Effect to handle select all after fetch
  useEffect(() => {
    if (shouldSelectAll && selectedStocks.length === totalItems && totalItems > 0) {
      setSelectedIds(selectedStocks.map(s => s.id))
      setShouldSelectAll(false)
    }
  }, [selectedStocks, shouldSelectAll, totalItems])

  const toggleSelectOne = (id) => {
    setSelectedIds(prev => 
      prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]
    )
  }

  const handleScroll = (e) => {
    if (isLoading || selectedStocks.length >= totalItems || searchTerm) return
    
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget
    if (scrollHeight - scrollTop <= clientHeight + 50) {
      onLoadMore()
    }
  }

  useEffect(() => {
    setIsConnected(thermalPrinter.isConnected)
    if (!thermalPrinter.isConnected && isOpen) {
      thermalPrinter.autoConnect().then(success => {
        if (success) setIsConnected(true)
      })
    }
  }, [isOpen])

  const isAllSelected = useMemo(() => {
    if (searchTerm) {
      return displayStocks.length > 0 && displayStocks.every(s => selectedIds.includes(s.id))
    }
    return selectedIds.length === totalItems && totalItems > 0
  }, [searchTerm, displayStocks, selectedIds, totalItems])

  const currentTotal = searchTerm ? displayStocks.length : totalItems

  if (!selectedStocks.length && !isLoading) return null

  return (
    <div className="d-flex flex-column" style={{ fontFamily: "'Inter', sans-serif", minHeight: '100%', backgroundColor: 'white' }}>
      {/* Fixed Header Area */}
      <div className="px-3 pt-4 pb-2 bg-white sticky-top shadow-sm" style={{ zIndex: 101, top: 0 }}>
        <div className="d-flex justify-content-between align-items-center mb-2">
          <h5 className="fw-bold text-dark mb-0" style={{ fontSize: '1.25rem' }}>
            {viewMode === 'selection' ? 'Pilih Inventori' : 'Pratinjau QR'}
          </h5>
          <CustomButton
            size="sm"
            outline
            color={viewMode === 'selection' ? 'primary' : 'secondary'}
            style={{ borderRadius: '8px', padding: '6px 12px' }}
            onClick={() => setViewMode(prev => prev === 'selection' ? 'preview' : 'selection')}
            disabled={viewMode === 'selection' && selectedIds.length === 0}
          >
            {viewMode === 'selection' ? (
              <div className="d-flex align-items-center gap-2">
                <span>Pratinjau</span>
                <ChevronRight size={14} />
              </div>
            ) : (
              <div className="d-flex align-items-center gap-2">
                <List size={14} />
                <span>Daftar</span>
              </div>
            )}
          </CustomButton>
        </div>
        
        <div className="d-flex justify-content-between align-items-center mb-3">
          <p className="text-muted mb-0 small">
            {viewMode === 'selection' 
              ? `${selectedIds.length} dari ${totalItems} item dipilih`
              : `Item ${currentIndex + 1} dari ${filteredStocks.length}`}
          </p>
        </div>

        {viewMode === 'selection' && (
          <>
            <div className="mb-3">
              <InputGroup className="shadow-sm border-0" style={{ overflow: 'hidden' }}>
                <InputGroupText className="bg-white border-end-0 pe-2">
                  <Search size={16} className="text-muted" />
                </InputGroupText>
                <Input 
                  placeholder="Cari nama atau ID..." 
                  className="border-start-0"
                  style={{ fontSize: '0.85rem', height: '40px' }}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </InputGroup>
            </div>

            <div className="d-flex justify-content-between align-items-center mb-1">
              <div className="d-flex align-items-center gap-2 cursor-pointer py-1" onClick={toggleSelectAll}>
                <div 
                  className={`d-flex align-items-center justify-content-center rounded`}
                  style={{ 
                    width: '18px', 
                    height: '18px', 
                    border: `2px solid ${isAllSelected ? '#0086C9' : '#D0D5DD'}`,
                    backgroundColor: isAllSelected ? '#0086C9' : 'transparent',
                    transition: 'all 0.2s'
                  }}
                >
                  {isLoading && selectedIds.length > 0 && selectedIds.length < totalItems ? (
                    <Spinner size="sm" style={{ width: '12px', height: '12px' }} />
                  ) : (
                    isAllSelected && <Check size={14} color="white" />
                  )}
                </div>
                <span className="small fw-bold text-dark">Pilih Semua {currentTotal > 0 && `(${currentTotal})`}</span>
              </div>
              {searchTerm && (
                <Badge color="light" className="text-muted fw-normal" style={{ fontSize: '0.7rem' }}>
                  {displayStocks.length} hasil
                </Badge>
              )}
            </div>
          </>
        )}
      </div>

      {/* Scrollable Content Area */}
      <div className="flex-grow-1 px-3 py-3 overflow-auto" onScroll={handleScroll} style={{ scrollbarWidth: 'thin' }}>
        {viewMode === 'selection' ? (
          <div className="selection-view">
            <div className="d-flex flex-column gap-2">
              {displayStocks.length > 0 ? (
                <>
                  {displayStocks.map((stock) => {
                    const isSelected = selectedIds.includes(stock.id);
                    return (
                      <div 
                        key={stock.id} 
                        className="p-2 rounded-3 border d-flex align-items-center justify-content-between cursor-pointer"
                        style={{ 
                          backgroundColor: isSelected ? '#F0F9FF' : 'white',
                          borderColor: isSelected ? '#B9E6FE' : '#F2F4F7',
                          transition: 'all 0.15s'
                        }}
                        onClick={() => toggleSelectOne(stock.id)}
                      >
                        <div className="d-flex align-items-center gap-2">
                          <div 
                            className={`d-flex align-items-center justify-content-center rounded`}
                            style={{ 
                              width: '16px', 
                              height: '16px', 
                              border: `2px solid ${isSelected ? '#0086C9' : '#D0D5DD'}`,
                              backgroundColor: isSelected ? '#0086C9' : 'transparent'
                            }}
                          >
                            {isSelected && <Check size={10} color="white" />}
                          </div>
                          <div>
                            <div className="fw-bold text-dark" style={{ fontSize: '0.8rem', lineHeight: '1.2' }}>
                              {getItemName(stock)}
                            </div>
                            <div className="text-muted" style={{ fontSize: '0.7rem' }}>
                              ID: {stock.id}
                            </div>
                          </div>
                        </div>
                        <Box size={14} className="text-muted opacity-25" />
                      </div>
                    )
                  })}
                  {isLoading && (
                    <div className="text-center py-2">
                      <Spinner size="sm" color="primary" />
                    </div>
                  )}
                </>
              ) : (
                <div className="text-center py-4">
                  {isLoading ? (
                    <Spinner color="primary" />
                  ) : (
                    <>
                      <Search size={32} className="text-muted opacity-25 mb-2" />
                      <p className="text-muted small">Tidak ditemukan</p>
                    </>
                  )}
                </div>
              )}
            </div>
          </div>
        ) : (
          <div className="preview-view">
            <Card className="border-0 shadow-sm mb-3" style={{ borderRadius: '16px', backgroundColor: '#F9FAFB', border: '1px solid #EAECF0' }}>
              <CardBody className="p-3">
                {filteredStocks.length > 0 ? (
                  <>
                    <div className="d-flex justify-content-between align-items-center mb-3">
                      <CustomButton
                        className="btn-icon bg-white shadow-sm border-0"
                        style={{ borderRadius: '10px', width: '36px', height: '36px' }}
                        onClick={() => setCurrentIndex(prev => Math.max(0, prev - 1))}
                        disabled={currentIndex === 0}
                      >
                        <ChevronLeft size={18} className={currentIndex === 0 ? 'text-muted' : 'text-primary'} />
                      </CustomButton>
                      
                      <span className="badge rounded-pill bg-white text-primary border shadow-sm px-2 py-1 fw-bold" style={{ fontSize: '0.7rem' }}>
                        {currentIndex + 1} / {filteredStocks.length}
                      </span>

                      <CustomButton
                        className="btn-icon bg-white shadow-sm border-0"
                        style={{ borderRadius: '10px', width: '36px', height: '36px' }}
                        onClick={() => setCurrentIndex(prev => Math.min(filteredStocks.length - 1, prev + 1))}
                        disabled={currentIndex === filteredStocks.length - 1}
                      >
                        <ChevronRight size={18} className={currentIndex === filteredStocks.length - 1 ? 'text-muted' : 'text-primary'} />
                      </CustomButton>
                    </div>

                    <div className="d-flex justify-content-center">
                      <div className="bg-white shadow p-3 text-center" style={{ width: '220px', borderRadius: '4px', border: '1px dashed #D0D5DD', position: 'relative' }}>
                        <div style={{ position: 'absolute', top: '-8px', left: '50%', transform: 'translateX(-50%)', width: '16px', height: '16px', backgroundColor: '#F9FAFB', borderRadius: '50%' }}></div>
                        <div style={{ position: 'absolute', bottom: '-8px', left: '50%', transform: 'translateX(-50%)', width: '16px', height: '16px', backgroundColor: '#F9FAFB', borderRadius: '50%' }}></div>

                        <div className={`mb-3 d-inline-block qr-container-${currentStock?.id}`}>
                          <QRCode
                            size={140}
                            value={FormatProcessor.generate('searchInventory', {
                              id: currentStock?.id,
                              name: getItemName(currentStock),
                              gtin: currentStock?.gtin || '',
                              expiry: currentStock?.expiry || '',
                              traceCode: currentStock?.traceCode || '',
                              url: getUrl(currentStock),
                              productDescription: getItemName(currentStock),
                            })}
                          />
                        </div>
                        <div className="border-top pt-2">
                          <h6 className="fw-bold text-dark mb-1" style={{ fontSize: '0.85rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {getItemName(currentStock)}
                          </h6>
                          <p className="text-muted extra-small mb-0">ID: {currentStock?.id || '-'}</p>
                        </div>
                      </div>
                    </div>
                  </>
                ) : (
                  <div className="py-4 text-center">
                    <Alert color="warning" className="small py-2 border-0 shadow-sm rounded-3">Pilih item dulu</Alert>
                  </div>
                )}
              </CardBody>
            </Card>
          </div>
        )}
      </div>

      {/* Fixed Footer Area */}
      <div className="px-3 pt-3 bg-white border-top shadow-lg sticky-bottom" style={{ zIndex: 101, bottom: 0, marginTop: 'auto' }}>
        {isPrinting && (
          <div className="mb-3 p-2 rounded-3" style={{ backgroundColor: '#ECFDF3', border: '1px solid #ABEFC6' }}>
            <div className="d-flex justify-content-between align-items-center mb-1">
              <span className="extra-small fw-bold text-success" style={{ fontSize: '0.7rem' }}>Mencetak...</span>
              <span className="extra-small fw-bold text-success" style={{ fontSize: '0.7rem' }}>{printProgress}%</span>
            </div>
            <Progress value={printProgress} color="success" style={{ height: '4px' }} className="progress-bar-animated" />
          </div>
        )}

        {!isConnected ? (
          <div className="p-2 rounded-3 bg-light border text-center mb-3">
            <div className="d-flex align-items-center justify-content-center gap-3 mb-2">
              <Printer size={20} className="text-muted opacity-50" />
              <div className="text-start">
                <h6 className="fw-bold text-dark mb-0" style={{ fontSize: '0.85rem' }}>Printer Belum Terhubung</h6>
                <p className="text-muted mb-0" style={{ fontSize: '0.7rem' }}>Hubungkan via Bluetooth</p>
              </div>
            </div>
            <CustomButton
              className="w-100 py-2 fw-bold d-flex align-items-center justify-content-center gap-2"
              style={{ backgroundColor: '#FDB022', border: 'none', color: 'white', borderRadius: '10px', fontSize: '0.85rem' }}
              onClick={handleToggleConnect}
              disabled={isConnecting}
            >
              {isConnecting ? <Spinner size="sm" /> : <RefreshCw size={16} />}
              {isConnecting ? 'Menghubungkan...' : 'Hubungkan Printer'}
            </CustomButton>
          </div>
        ) : (
          <div className="mb-3">
            <div className="d-flex align-items-center gap-2 mb-2">
              <div style={{ width: '6px', height: '6px', borderRadius: '50%', backgroundColor: '#12B76A' }}></div>
              <span className="text-success fw-bold" style={{ fontSize: '0.75rem' }}>Printer Terhubung</span>
            </div>
            <div className="d-flex gap-2">
              <CustomButton
                className="flex-fill py-2 fw-bold d-flex align-items-center justify-content-center gap-2"
                style={{ backgroundColor: '#0086C9', border: 'none', color: 'white', borderRadius: '10px', fontSize: '0.85rem' }}
                onClick={handlePrintAll}
                disabled={isPrinting || selectedIds.length === 0}
              >
                <Printer size={16} /> 
                <span>Cetak ({selectedIds.length})</span>
              </CustomButton>
              {viewMode === 'preview' && (
                <CustomButton
                  className="flex-fill py-2 fw-bold d-flex align-items-center justify-content-center gap-2"
                  style={{ backgroundColor: '#12B76A', border: 'none', color: 'white', borderRadius: '10px', fontSize: '0.85rem' }}
                  onClick={handlePrintOne}
                  disabled={isPrinting || !currentStock}
                >
                  <Printer size={16} /> 
                  <span>Item Ini</span>
                </CustomButton>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Hidden QR Rendering Area - Matches TransactionQRPrint logic */}
      <div style={{ position: 'fixed', left: '-9999px', top: 0, opacity: 0, pointerEvents: 'none' }}>
        <img ref={logoRef} src={pmlogo} alt="logo-hidden" />
        {filteredStocks.map((stock) => (
          <div key={`hidden-qr-${stock.id}`} ref={(el) => (qrRefs.current[stock.id] = el)}>
            <QRCode
              size={150}
              value={FormatProcessor.generate('searchInventory', {
                id: stock?.id,
                name: getItemName(stock),
                gtin: stock?.gtin || '',
                expiry: stock?.expiry || '',
                traceCode: stock?.traceCode || '',
                url: getUrl(stock),
                productDescription: getItemName(stock),
              })}
            />
          </div>
        ))}
      </div>

      <style dangerouslySetInnerHTML={{ __html: `
        @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
        .spin-animation { animation: spin 2s linear infinite; }
        .cursor-pointer { cursor: pointer; }
        .extra-small { font-size: 0.75rem; }
        .overflow-auto::-webkit-scrollbar { width: 3px; }
        .overflow-auto::-webkit-scrollbar-track { background: transparent; }
        .overflow-auto::-webkit-scrollbar-thumb { background: #e0e0e0; border-radius: 10px; }
      `}} />
    </div>
  )
}
